import { Controller } from 'react-hook-form';
import { Textarea } from '@monksflow/monks-ui';
import { BookUserIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getBiographyItemLabel } from '../../../model';
import {
  FormSectionV2,
  FormSubsectionV2,
  PersonaFieldLabel,
  PersonaTagField,
} from '../../PersonaFormSectionsV2';
import { isFieldRequired, isNestedFieldRequired } from '../EditPersonaForm.schema';
import { useEditPersonaFormContext } from '../hooks/useEditPersonaFormContext';

export function BiographySectionV2() {
  const { t } = useTranslation('audiences');
  const { control, biographyItemsArray } = useEditPersonaFormContext();
  const { fields } = biographyItemsArray;

  return (
    <FormSectionV2
      icon={<BookUserIcon className="size-5" />}
      title={t('audiences.biographySection.biography')}
    >
      <Controller
        name="interests"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <PersonaTagField
            value={field.value}
            onChange={field.onChange}
            label={t('audiences.biographySection.interests')}
            placeholder={t('audiences.biographySection.typeAndPressEnterTo')}
            required={isFieldRequired(field.name)}
            error={error?.message}
            options={[]}
          />
        )}
      />

      <FormSubsectionV2>{t('audiences.biographySection.biographyItems')}</FormSubsectionV2>

      <div className="flex flex-col gap-sm">
        <div className="flex items-center gap-sm">
          <div className="w-1/3">
            <PersonaFieldLabel required={isNestedFieldRequired('biographyItems', 'item')}>
              {t('audiences.biographySection.item')}
            </PersonaFieldLabel>
          </div>
          <div className="flex-1">
            <PersonaFieldLabel required={isNestedFieldRequired('biographyItems', 'description')}>
              {t('audiences.biographySection.description')}
            </PersonaFieldLabel>
          </div>
        </div>

        {/* The item is a fixed taxonomy entry, so only its description is editable. */}
        {fields.map((field, index) => (
          <div key={field.id} className="flex items-start gap-sm">
            <span className="w-1/3 pt-2 text-sm text-foreground">
              {getBiographyItemLabel(fields[index].item)}
            </span>
            <div className="flex-1">
              <Controller
                name={`biographyItems.${index}.description`}
                control={control}
                render={({ field: descField, fieldState: { error } }) => (
                  <>
                    <Textarea
                      {...descField}
                      rows={2}
                      placeholder={t('audiences.biographySection.description')}
                      aria-label={getBiographyItemLabel(fields[index].item)}
                      aria-invalid={Boolean(error) || undefined}
                    />
                    {error?.message && (
                      <p className="mt-3xs text-xs text-destructive">{error.message}</p>
                    )}
                  </>
                )}
              />
            </div>
          </div>
        ))}
      </div>
    </FormSectionV2>
  );
}

BiographySectionV2.displayName = 'BiographySectionV2';
